import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

import { Loan, Customer, InventoryItem, useLoansStore, useCustomersStore, useRevenueStore, useInventoryStore } from '@/hooks/useSupabaseStore';
import PrintLoanTicket from './components/PrintLoanTicket';
import InventoryModal from './components/InventoryModal';
import LoanHistoryPanel from './components/LoanHistory';
import CustomerViewModal from './components/CustomerViewModal';
import { useExportLoans } from '@/hooks/useExportLoans';
import { useShop } from '@/hooks/useShop';
import { shopCategories, shopWarehouses } from '@/lib/brand';

const statusConfig = {
  active: { label: 'Đang vay', className: 'bg-emerald-500 text-white' },
  overdue: { label: 'Quá hạn', className: 'bg-red-500 text-white' },
  due_soon: { label: 'Sắp đến hạn', className: 'bg-amber-500 text-white' },
  interest_paid: { label: 'Đã đóng lãi', className: 'bg-teal-500 text-white' },
  redeemed: { label: 'Đã chuộc', className: 'bg-gray-500 text-white' },
  forfeited: { label: 'Tịch thu', className: 'bg-rose-500 text-white' },
};

function makeEmptyLoanForm(interestRate: number, assetCategory: string): Omit<Loan, 'id'> {
  return {
    customerId: '', customerName: '', customerPhone: '',
    assetName: '', assetCategory,
    loanAmount: 0, interestRate,
    startDate: new Date().toISOString().slice(0, 10),
    dueDate: '', status: 'active',
    warehouse: '',
  };
}

const emptyCustomerForm: Omit<Customer, 'id'> = {
  name: '', phone: '', idCard: '', address: '',
  createdAt: new Date().toISOString().slice(0, 10),
  totalLoans: 0, activeLoans: 0, totalBorrowed: 0,
};

function addDaysToDate(dateStr: string, days: number): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function LoansPage() {
  const { loans, addLoan, updateLoan, deleteLoan, loading } = useLoansStore();
  const { customers, addCustomer } = useCustomersStore();
  const { addRevenueLog, logs: revenueLogs } = useRevenueStore();
  const { items, addItem, updateItem } = useInventoryStore();
  const { exportLoans, exporting } = useExportLoans();
  const { shop } = useShop();
  const categories = shopCategories(shop);
  const warehouses = shopWarehouses(shop);
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterWarehouse, setFilterWarehouse] = useState('all');
  const [sortByStatus, setSortByStatus] = useState(false);
  const [viewLoan, setViewLoan] = useState<Loan | null>(null);
  const [viewCustomer, setViewCustomer] = useState<Customer | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [toast, setToast] = useState('');

  // Add Loan modal
  const [showAdd, setShowAdd] = useState(false);
  const [loanForm, setLoanForm] = useState<Omit<Loan, 'id'>>(() => makeEmptyLoanForm(3, 'Vàng bạc'));
  const [startTime, setStartTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // Add Customer modal (standalone)
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [customerForm, setCustomerForm] = useState<Omit<Customer, 'id'>>(emptyCustomerForm);

  // New Customer inline (inside loan form)
  const [showNewCustomerInline, setShowNewCustomerInline] = useState(false);
  const [inlineCustomerForm, setInlineCustomerForm] = useState<Omit<Customer, 'id'>>(emptyCustomerForm);

  // In phiếu
  const [printLoan, setPrintLoan] = useState<Loan | null>(null);

  // Chỉnh sửa lãi suất
  const [editingInterest, setEditingInterest] = useState(false);
  const [editInterestValue, setEditInterestValue] = useState(0);

  // Modal tồn kho
  const [showInventoryModal, setShowInventoryModal] = useState(false);

  // Chọn kho inline trong table / mobile card
  const [editingWarehouseFor, setEditingWarehouseFor] = useState<string | null>(null);

  // Toggle ẩn/hiện tổng khoản vay + tổng giá trị + thống kê danh mục
  const [showLoanSummary, setShowLoanSummary] = useState(false);
  const [showLoanValue, setShowLoanValue] = useState(false);
  const [showCategoryStats, setShowCategoryStats] = useState(false);

  // Auto-format giá trị vay trong modal tạo mới
  const [loanAmountStr, setLoanAmountStr] = useState('');

  // Chỉnh sửa inline các trường trong chi tiết
  type EditableField = 'customerName' | 'customerPhone' | 'assetName' | 'assetCategory' | 'loanAmount' | 'startDate' | 'dueDate';
  const [editingField, setEditingField] = useState<EditableField | null>(null);
  const [editFieldValue, setEditFieldValue] = useState<string>('');

  // Gia hạn
  const [extendLoan, setExtendLoan] = useState<Loan | null>(null);
  const [extendType, setExtendType] = useState<'days' | 'months'>('days');
  const [extendValue, setExtendValue] = useState(30);
  const [extendNote, setExtendNote] = useState('');
  const [extendStartDate, setExtendStartDate] = useState('');

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const borrowedDays = (startDate: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(startDate.slice(0, 10));
    start.setHours(0, 0, 0, 0);
    return Math.max(1, Math.ceil((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
  };

  const overdueDays = (dueDate: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return Math.max(1, Math.ceil((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24)));
  };

  // ===== TỰ ĐỘNG CẬP NHẬT TRẠNG THÁI =====
  const autoUpdateRef = useRef(false);
  useEffect(() => {
    if (autoUpdateRef.current || loans.length === 0) return;
    autoUpdateRef.current = true;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const updates: Promise<void>[] = [];
    let updatedCount = 0;

    loans.forEach(loan => {
      // Chỉ tự động cập nhật các khoản đang hoạt động (không động vào đã chuộc/tịch thu)
      if (loan.status === 'redeemed' || loan.status === 'forfeited') return;

      const dueDate = new Date(loan.dueDate);
      dueDate.setHours(0, 0, 0, 0);
      const diffDays = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

      let newStatus: Loan['status'] | null = null;

      if (diffDays < 0) {
        // Quá hạn
        if (loan.status !== 'overdue') newStatus = 'overdue';
      } else if (diffDays <= 7) {
        // Sắp đến hạn (trong vòng 7 ngày)
        if (loan.status !== 'due_soon') newStatus = 'due_soon';
      } else {
        // Còn hạn bình thường
        if (loan.status === 'overdue' || loan.status === 'due_soon') newStatus = 'active';
      }

      if (newStatus) {
        updatedCount++;
        updates.push(updateLoan(loan.id, { status: newStatus }));
      }
    });

    if (updates.length > 0) {
      Promise.all(updates).then(() => {
        if (updatedCount > 0) {
          showToast(`Đã tự động cập nhật trạng thái ${updatedCount} khoản vay.`);
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loans.length]);

  const statusPriority: Record<string, number> = {
    overdue: 1, due_soon: 2, active: 3, interest_paid: 4, forfeited: 5, redeemed: 6,
  };

  // redeemed / forfeited luôn xuống cuối dù không bật sort
  const completedPriority = (s: Loan['status']) => (s === 'redeemed' || s === 'forfeited') ? 2 : 1;

  const filtered = loans
    .filter(l => {
      const matchSearch = l.customerName.toLowerCase().includes(search.toLowerCase()) ||
        l.id.toLowerCase().includes(search.toLowerCase()) ||
        l.assetName.toLowerCase().includes(search.toLowerCase());
      const matchStatus = filterStatus === 'all' || l.status === filterStatus;
      const matchCategory = filterCategory === 'all' || l.assetCategory === filterCategory;
      const matchWarehouse = filterWarehouse === 'all' || l.warehouse === filterWarehouse;
      return matchSearch && matchStatus && matchCategory && matchWarehouse;
    })
    .sort((a, b) => {
      // Bước 1: Đẩy redeemed/forfeited xuống cuối
      const pa = completedPriority(a.status);
      const pb = completedPriority(b.status);
      if (pa !== pb) return pa - pb;

      // Bước 2: Nếu đang bật sort theo trạng thái chi tiết
      if (sortByStatus) {
        return (statusPriority[a.status] ?? 99) - (statusPriority[b.status] ?? 99);
      }

      return 0;
    });

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.phone.includes(customerSearch)
  );

  const selectCustomer = (c: Customer) => {
    setLoanForm(prev => ({ ...prev, customerId: c.id, customerName: c.name, customerPhone: c.phone }));
    setCustomerSearch(c.name);
    setShowCustomerDropdown(false);
  };

  const handleCreateInlineCustomer = async () => {
    if (!inlineCustomerForm.name || !inlineCustomerForm.phone) {
      showToast('Vui lòng nhập tên và số điện thoại!');
      return;
    }
    const newId = `KH${Date.now()}`;
    const newCustomer: Customer = { ...inlineCustomerForm, id: newId };
    try {
      await addCustomer(newCustomer);
      setLoanForm(prev => ({ ...prev, customerId: newId, customerName: newCustomer.name, customerPhone: newCustomer.phone }));
      setCustomerSearch(newCustomer.name);
      setShowNewCustomerInline(false);
      setInlineCustomerForm(emptyCustomerForm);
      showToast('Đã tạo khách hàng mới!');
    } catch {
      showToast('Lỗi: Không thể tạo khách hàng. Vui lòng thử lại!');
    }
  };

  const handleAddLoan = async () => {
    if (!loanForm.customerName || !loanForm.assetName || !loanForm.loanAmount || !loanForm.dueDate) {
      showToast('Vui lòng điền đầy đủ thông tin!');
      return;
    }
    const now = new Date();
    const datePart = `${String(now.getFullYear()).slice(2)}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const seqPart = String(now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()).padStart(5, '0');
    const newId = `KV${datePart}-${seqPart}`;
    // Ghép ngày + giờ vay thành "YYYY-MM-DD HH:mm"
    const startDateTime = `${loanForm.startDate} ${startTime}`;
    try {
      await addLoan({ ...loanForm, startDate: startDateTime, id: newId });

      // Tự động tạo inventory item tương ứng
      const newItemId = `HH${Date.now()}`;
      const newItem: InventoryItem = {
        id: newItemId,
        name: loanForm.assetName,
        category: loanForm.assetCategory,
        description: '',
        estimatedValue: loanForm.loanAmount,
        loanAmount: loanForm.loanAmount,
        status: 'pawned',
        loanId: newId,
        customerName: loanForm.customerName,
        receivedDate: loanForm.startDate,
        condition: 'good',
        warehouse: loanForm.warehouse || '',
      };
      await addItem(newItem);

      setShowAdd(false);
      setLoanForm(makeEmptyLoanForm(shop?.defaultInterestRate ?? 3, categories[0] || ''));
      setCustomerSearch('');
      setShowNewCustomerInline(false);
      setLoanAmountStr('');
      const resetNow = new Date();
      setStartTime(`${String(resetNow.getHours()).padStart(2, '0')}:${String(resetNow.getMinutes()).padStart(2, '0')}`);
      showToast('Thêm khoản vay thành công! Tài sản đã được thêm vào tồn kho.');
    } catch {
      showToast('Lỗi: Không thể thêm khoản vay. Vui lòng thử lại!');
    }
  };

  const handleAddCustomer = async () => {
    if (!customerForm.name || !customerForm.phone || !customerForm.idCard) {
      showToast('Vui lòng điền đầy đủ thông tin bắt buộc!');
      return;
    }
    const newId = `KH${Date.now()}`;
    try {
      await addCustomer({ ...customerForm, id: newId });
      setShowAddCustomer(false);
      setCustomerForm(emptyCustomerForm);
      showToast('Thêm khách hàng thành công!');
    } catch {
      showToast('Lỗi: Không thể thêm khách hàng. Vui lòng thử lại!');
    }
  };

  const handleDelete = async (id: string) => {
    const loan = loans.find(l => l.id === id);
    await deleteLoan(id);

    setDeleteConfirm(null);
    setViewLoan(null);
    showToast('Đã xóa khoản vay! Tài sản và doanh thu liên kết đã được dọn sạch.');
  };

  const handleStatusChange = async (id: string, status: Loan['status']) => {
    const loan = loans.find(l => l.id === id);
    await updateLoan(id, { status });
    if (viewLoan?.id === id) setViewLoan(prev => prev ? { ...prev, status } : null);

    if (status === 'redeemed' && loan) {
      // Cập nhật tồn kho

      // Cập nhật tồn kho
      const linkedItem = items.find(i => i.loanId === id && i.status === 'pawned');
      if (linkedItem) await updateItem(linkedItem.id, { status: 'redeemed' });

      // Tính lãi thực tế từ ngày vay đến hôm nay
      const today = new Date();
      const startD = new Date(loan.startDate);
      const actualDays = Math.max(1, Math.ceil((today.getTime() - startD.getTime()) / (1000 * 60 * 60 * 24)));
      const interestPerDay = loan.loanAmount * loan.interestRate / 100 / 30;
      const actualInterest = Math.round(interestPerDay * actualDays);

      // Tổng lãi đã thu qua gia hạn của khoản vay này
      const alreadyCollected = revenueLogs
        .filter(l => l.loanId === id && l.type === 'extend')
        .reduce((sum, l) => sum + l.interestAmount, 0);

      // Lãi còn lại chưa thu (lãi thực - lãi đã thu qua gia hạn)
      const remainInterest = actualInterest - alreadyCollected;

      // Ghi vào doanh thu: nếu dương = còn thu thêm, nếu âm = đã thu dư (trừ lại)
      await addRevenueLog({
        id: `REV${Date.now()}`,
        loanId: id,
        customerName: loan.customerName,
        assetName: loan.assetName,
        type: 'redeem',
        interestAmount: remainInterest,
        extendInterest: 0,
        oldInterest: alreadyCollected,
        totalPayback: loan.loanAmount + actualInterest,
        extendDays: actualDays,
        note: `Chuộc đồ - lãi thực ${actualInterest.toLocaleString('vi-VN')}đ, đã thu ${alreadyCollected.toLocaleString('vi-VN')}đ`,
        createdAt: new Date().toISOString(),
      });

      const msg = remainInterest >= 0
        ? `Đã chuộc! Thu thêm lãi ${remainInterest.toLocaleString('vi-VN')}đ vào doanh thu.`
        : `Đã chuộc! Hoàn lại ${Math.abs(remainInterest).toLocaleString('vi-VN')}đ lãi thu dư.`;
      showToast(msg);

    } else if (status === 'interest_paid' && loan) {
      // Lãi 1 tháng = vốn × lãi suất%
      const monthlyInterest = Math.round(loan.loanAmount * loan.interestRate / 100);

      // Tự động gia hạn thêm 1 tháng (30 ngày) từ ngày hạn trả hiện tại
      const newDueDate = addDaysToDate(loan.dueDate, 30);
      await updateLoan(id, { status: 'active', dueDate: newDueDate });
      if (viewLoan?.id === id) setViewLoan(prev => prev ? { ...prev, status: 'active', dueDate: newDueDate } : null);

      await addRevenueLog({
        id: `REV${Date.now()}`,
        loanId: id,
        customerName: loan.customerName,
        assetName: loan.assetName,
        type: 'extend',
        interestAmount: monthlyInterest,
        extendInterest: monthlyInterest,
        oldInterest: 0,
        totalPayback: monthlyInterest,
        extendDays: 30,
        note: `Đóng lãi 1 tháng — vốn ${loan.loanAmount.toLocaleString('vi-VN')}đ × ${loan.interestRate}%. Gia hạn đến ${newDueDate}`,
        createdAt: new Date().toISOString(),
      });

      showToast(`Đã đóng lãi! Thu ${monthlyInterest.toLocaleString('vi-VN')}đ. Hợp đồng gia hạn đến ${newDueDate}.`);

    } else if (status === 'forfeited') {
      const linkedItem = items.find(i => i.loanId === id && i.status === 'pawned');
      if (linkedItem) await updateItem(linkedItem.id, { status: 'forfeited' });
      showToast('Đã tịch thu tài sản! Tồn kho đã cập nhật.');
    } else {
      showToast('Cập nhật trạng thái thành công!');
    }
  };

  const handleExtend = async () => {
    if (!extendLoan) return;
    const days = extendType === 'days' ? extendValue : extendValue * 30;
    const newDueDate = addDaysToDate(extendLoan.dueDate, days);
    const updates: Partial<Loan> = { dueDate: newDueDate, status: 'active' };
    if (extendStartDate && extendStartDate !== extendLoan.startDate) {
      updates.startDate = extendStartDate;
    }

    // Tính lãi để ghi vào doanh thu
    const interestPerDay = extendLoan.loanAmount * extendLoan.interestRate / 100 / 30;
    const extInterest = Math.round(interestPerDay * days);
    const startD = new Date(extendStartDate || extendLoan.startDate);
    const oldDueD = new Date(extendLoan.dueDate);
    const oldDays = Math.max(0, Math.ceil((oldDueD.getTime() - startD.getTime()) / (1000 * 60 * 60 * 24)));
    const oldInterest = Math.round(interestPerDay * oldDays);
    const totalInterest = oldInterest + extInterest;
    const totalPayback = extendLoan.loanAmount + totalInterest;

    try {
      await updateLoan(extendLoan.id, updates);
      await addRevenueLog({
        id: `REV${Date.now()}`,
        loanId: extendLoan.id,
        customerName: extendLoan.customerName,
        assetName: extendLoan.assetName,
        type: 'extend',
        interestAmount: extInterest,
        extendInterest: extInterest,
        oldInterest,
        totalPayback,
        extendDays: days,
        note: extendNote,
        createdAt: new Date().toISOString(),
      });
      if (viewLoan?.id === extendLoan.id) setViewLoan(prev => prev ? { ...prev, ...updates } : null);
      showToast(`Đã gia hạn đến ${newDueDate}! Lãi +${extInterest.toLocaleString('vi-VN')}đ vào doanh thu`);
    } catch {
      showToast('Lỗi: Không thể gia hạn. Vui lòng thử lại!');
    }
    setExtendLoan(null);
    setExtendValue(30);
    setExtendNote('');
    setExtendType('days');
    setExtendStartDate('');
  };

  const handleUpdateInterestRate = async () => {
    if (!viewLoan) return;
    if (editInterestValue <= 0 || editInterestValue > 100) {
      showToast('Lãi suất không hợp lệ!');
      return;
    }
    try {
      await updateLoan(viewLoan.id, { interestRate: editInterestValue });
      setViewLoan(prev => prev ? { ...prev, interestRate: editInterestValue } : null);
      setEditingInterest(false);
      showToast(`Đã cập nhật lãi suất thành ${editInterestValue}%/tháng!`);
    } catch {
      showToast('Lỗi: Không thể cập nhật lãi suất. Vui lòng thử lại!');
    }
  };

  const openEditInterest = (loan: Loan) => {
    setEditInterestValue(loan.interestRate);
    setEditingInterest(true);
  };

  const openEditField = (field: EditableField, value: string | number) => {
    setEditingField(field);
    if (field === 'loanAmount') {
      setEditFieldValue(typeof value === 'number' ? value.toLocaleString('vi-VN') : String(value));
    } else {
      setEditFieldValue(String(value));
    }
    setEditingInterest(false);
  };

  const handleSaveField = async () => {
    if (!viewLoan || !editingField) return;
    let parsedValue: string | number = editFieldValue;
    if (editingField === 'loanAmount') {
      const rawNum = Number(editFieldValue.replace(/\D/g, ''));
      if (!rawNum || rawNum <= 0) { showToast('Giá trị vay không hợp lệ!'); return; }
      parsedValue = rawNum;
    }
    if ((editingField === 'customerName' || editingField === 'assetName') && !editFieldValue.trim()) {
      showToast('Không được để trống!'); return;
    }
    try {
      await updateLoan(viewLoan.id, { [editingField]: parsedValue });
      setViewLoan(prev => prev ? { ...prev, [editingField]: parsedValue } : null);
      setEditingField(null);
      showToast('Đã cập nhật thành công!');
    } catch {
      showToast('Lỗi: Không thể cập nhật. Vui lòng thử lại!');
    }
  };

  const cancelEditField = () => { setEditingField(null); setEditFieldValue(''); };

  const openConfiscate = async (loan: Loan) => {
    if (loan.status !== 'forfeited') {
      try {
        await updateLoan(loan.id, { status: 'forfeited' });
        const linkedItem = items.find(i => i.loanId === loan.id && i.status === 'pawned');
        if (linkedItem) await updateItem(linkedItem.id, { status: 'forfeited' });
        showToast('Đã đánh dấu khoản vay thành Tịch thu!');
      } catch {
        showToast('Lỗi khi cập nhật trạng thái tịch thu!');
        return;
      }
    }
    // Chuyển sang trang Tịch Thu — tự động lấy từ loans với status=forfeited
    navigate('/confiscations');
  };

  const handleChangeWarehouse = async (loanId: string, warehouse: string) => {
    try {
      await updateLoan(loanId, { warehouse });
      const linkedItem = items.find(i => i.loanId === loanId && i.status === 'pawned');
      if (linkedItem) await updateItem(linkedItem.id, { warehouse });
      showToast(warehouse ? `Đã chuyển vào ${warehouse}!` : 'Đã bỏ chọn kho!');
    } catch {
      showToast('Lỗi: Không thể cập nhật kho. Vui lòng thử lại!');
    }
    setEditingWarehouseFor(null);
  };

  const openExtend = (loan: Loan) => {
    setExtendLoan(loan);
    setExtendType('days');
    setExtendValue(30);
    setExtendNote('');
    setExtendStartDate(loan.startDate);
  };

  return (<>
      {toast && (
        <div className="fixed top-5 right-5 z-[100] bg-gray-800 text-white px-4 py-2.5 rounded-xl text-sm font-medium shadow-lg">
          {toast}
        </div>
      )}

      {/* Toggle: Tổng khoản vay */}
      <div className="mb-4 md:mb-6">
        <button
          onClick={() => setShowLoanSummary(v => !v)}
          className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors cursor-pointer ${showLoanSummary ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
        >
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-stack-line"></i></span>
            Tổng khoản vay
          </span>
          <span className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-arrow-down-s-line transition-transform ${showLoanSummary ? 'rotate-180' : ''}`}></i>
          </span>
        </button>
        {showLoanSummary && (() => {
          const total = loans.length;
          const active = loans.filter(l => l.status !== 'redeemed' && l.status !== 'forfeited').length;
          const dueSoon = loans.filter(l => l.status === 'due_soon').length;
          const overdue = loans.filter(l => l.status === 'overdue').length;
          return (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
              <div className="bg-white rounded-xl p-4">
                <p className="text-gray-400 text-xs mb-1">Tổng khoản vay</p>
                <p className="font-bold text-xl text-gray-800">{total}</p>
                <p className="text-xs text-gray-400 mt-0.5">khoản</p>
              </div>
              <div className="bg-white rounded-xl p-4">
                <p className="text-gray-400 text-xs mb-1">Đang hoạt động</p>
                <p className="font-bold text-xl text-emerald-600">{active}</p>
                <p className="text-xs text-gray-400 mt-0.5">khoản</p>
              </div>
              <div className="bg-white rounded-xl p-4">
                <p className="text-gray-400 text-xs mb-1">Sắp đến hạn</p>
                <p className="font-bold text-xl text-amber-600">{dueSoon}</p>
                <p className="text-xs text-gray-400 mt-0.5">khoản</p>
              </div>
              <div className="bg-white rounded-xl p-4">
                <p className="text-gray-400 text-xs mb-1">Quá hạn</p>
                <p className="font-bold text-xl text-red-600">{overdue}</p>
                <p className="text-xs text-gray-400 mt-0.5">khoản</p>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Toggle: Tổng giá trị cho vay */}
      <div className="mb-4 md:mb-6">
        <button
          onClick={() => setShowLoanValue(v => !v)}
          className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors cursor-pointer ${showLoanValue ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
        >
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-money-cny-circle-line"></i></span>
            Tổng giá trị cho vay
          </span>
          <span className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-arrow-down-s-line transition-transform ${showLoanValue ? 'rotate-180' : ''}`}></i>
          </span>
        </button>
        {showLoanValue && (() => {
          const activeLoans = loans.filter(l => l.status !== 'redeemed' && l.status !== 'forfeited');
          const totalActiveValue = activeLoans.reduce((sum, l) => sum + l.loanAmount, 0);
          const totalAllValue = loans.reduce((sum, l) => sum + l.loanAmount, 0);
          return (
            <div className="bg-white rounded-xl p-3 md:p-4 mt-2 flex flex-wrap items-center gap-4">
              <div>
                <p className="text-gray-400 text-xs">Tổng giá trị đang cho vay (không tính đã chuộc &amp; tịch thu)</p>
                <p className="font-bold text-lg md:text-xl text-gray-800">{totalActiveValue.toLocaleString('vi-VN')}đ</p>
              </div>
              <div className="hidden md:block w-px h-8 bg-gray-200"></div>
              <div>
                <p className="text-gray-400 text-xs">Tổng giá trị toàn bộ</p>
                <p className="font-bold text-sm text-gray-500">{totalAllValue.toLocaleString('vi-VN')}đ</p>
              </div>
              <div className="hidden md:block w-px h-8 bg-gray-200"></div>
              <div>
                <p className="text-gray-400 text-xs">Đã chuộc / Tịch thu</p>
                <p className="font-bold text-sm text-gray-500">{loans.filter(l => l.status === 'redeemed').length} / {loans.filter(l => l.status === 'forfeited').length}</p>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Toggle: Thống kê theo danh mục */}
      <div className="mb-4 md:mb-6">
        <button
          onClick={() => setShowCategoryStats(v => !v)}
          className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors cursor-pointer ${showCategoryStats ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
        >
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-bar-chart-grouped-line"></i></span>
            Thống kê theo danh mục tài sản
          </span>
          <span className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-arrow-down-s-line transition-transform ${showCategoryStats ? 'rotate-180' : ''}`}></i>
          </span>
        </button>
        {showCategoryStats && (
          <div className="bg-white rounded-xl p-3 md:p-4 mt-2">
            <div className="flex items-center gap-2 mb-2">
              <p className="text-gray-400 text-xs font-medium">Thống kê theo danh mục tài sản</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {Array.from(new Set(loans.map(l => l.assetCategory))).sort().map(cat => {
                const count = loans.filter(l => l.assetCategory === cat && l.status !== 'redeemed' && l.status !== 'forfeited').length;
                const total = loans.filter(l => l.assetCategory === cat && l.status !== 'redeemed' && l.status !== 'forfeited').reduce((sum, l) => sum + l.loanAmount, 0);
                return (
                  <div key={cat} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-xs font-semibold text-gray-700">{cat}</span>
                    <span className="text-xs bg-[#f59e0b] text-white px-1.5 py-0.5 rounded-md font-bold">{count}</span>
                    <span className="text-xs text-gray-400">{total.toLocaleString('vi-VN')}đ</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl overflow-hidden">
        {/* Filter + Search bar */}
        <div className="flex flex-col gap-3 px-4 md:px-5 py-3 md:py-4 border-b border-gray-100">
          {/* Status filters */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {['all', 'active', 'due_soon', 'overdue', 'redeemed'].map(s => (
              <button key={s} onClick={() => setFilterStatus(s)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${filterStatus === s ? 'bg-[#f59e0b] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {s === 'all' ? 'Tất cả' : statusConfig[s as keyof typeof statusConfig]?.label}
              </button>
            ))}
            <div className="w-px h-4 bg-gray-300 mx-1 hidden sm:block"></div>
            <button
              onClick={() => setSortByStatus(v => !v)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap border ${sortByStatus ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
              title={sortByStatus ? 'Đang sắp xếp: Quá hạn → Sắp đến hạn → Đang vay → Đã đóng lãi → Tịch thu → Đã chuộc' : 'Bấm để sắp xếp theo trạng thái'}
            >
              <span className="w-3 h-3 flex items-center justify-center"><i className={`ri-arrow-up-down-line ${sortByStatus ? 'text-white' : 'text-gray-500'}`}></i></span>
              {sortByStatus ? 'Đang sắp xếp theo TT' : 'Sắp xếp theo TT'}
            </button>
          </div>
          {/* Category filters */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs text-gray-400 font-medium shrink-0">Danh mục:</span>
            {['all', ...categories].map(cat => (
              <button key={cat} onClick={() => setFilterCategory(cat)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${filterCategory === cat ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {cat === 'all' ? 'Tất cả' : cat}
              </button>
            ))}
          </div>
          {/* Warehouse filters */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs text-gray-400 font-medium shrink-0">Kho:</span>
            {['all', ...warehouses].map(kho => (
              <button key={kho} onClick={() => setFilterWarehouse(kho)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${filterWarehouse === kho ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {kho === 'all' ? 'Tất cả' : kho}
              </button>
            ))}
          </div>
          {/* Search + Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[140px]">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400"><i className="ri-search-line text-sm"></i></span>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Tìm kiếm..." className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]" />
            </div>
            <button onClick={() => setShowAddCustomer(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line text-base"></i></span>
              <span className="hidden sm:inline">Tạo khách hàng</span>
            </button>
            <button onClick={() => exportLoans(filtered, 'excel')}
              disabled={exporting}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-file-excel-2-line text-base text-emerald-600"></i></span>
              <span className="hidden sm:inline">Excel</span>
            </button>
            <button onClick={() => exportLoans(filtered, 'pdf')}
              disabled={exporting}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-file-pdf-line text-base text-red-500"></i></span>
              <span className="hidden sm:inline">PDF</span>
            </button>
            <button onClick={() => setShowInventoryModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-archive-2-line text-base text-amber-600"></i></span>
              <span className="hidden sm:inline">Tồn kho</span>
            </button>
            <button onClick={() => { setShowAdd(true); setCustomerSearch(''); setLoanForm(makeEmptyLoanForm(shop?.defaultInterestRate ?? 3, categories[0] || '')); setShowNewCustomerInline(false); setLoanAmountStr(''); }}
              className="flex items-center gap-1.5 px-3 py-2 bg-[#f59e0b] text-white rounded-lg text-sm font-semibold hover:bg-[#d97706] transition-colors cursor-pointer whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-base"></i></span>
              <span className="hidden sm:inline">Tạo khoản vay</span>
            </button>
          </div>
        </div>

        {/* Mobile: Card list */}
        <div className="block md:hidden divide-y divide-gray-50">
          {filtered.map(loan => {
            const st = statusConfig[loan.status];
            return (
              <div key={loan.id} className="px-4 py-3.5 hover:bg-amber-50/80 transition-colors cursor-default">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <span className="text-xs font-bold text-[#f59e0b]">{loan.id}</span>
                    <p
                      className="text-sm font-semibold text-gray-800 mt-0.5 cursor-pointer hover:text-amber-600 transition-colors"
                      onClick={() => {
                        const c = customers.find(cu => cu.id === loan.customerId);
                        if (c) setViewCustomer(c);
                      }}
                    >{loan.customerName}</p>
                    <p className="text-xs text-gray-400">{loan.customerPhone}</p>
                  </div>
                  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap shrink-0 ${st.className}`}>
                    {st.label}
                    {loan.status === 'overdue' && (
                      <span className="ml-1 opacity-90">
                        · {overdueDays(loan.dueDate)} ngày
                      </span>
                    )}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-3">
                  <div>
                    <p className="text-xs text-gray-400">Tài sản</p>
                    <p className="text-xs font-medium text-gray-700 truncate">{loan.assetName}</p>
                    {(loan.assetCategory === 'Xe máy' || loan.assetCategory === 'Ô tô') && loan.warehouse && (
                      <p className="text-[10px] text-amber-600 font-medium mt-0.5">{loan.warehouse}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Giá trị vay</p>
                    <p className="text-xs font-semibold text-gray-800">{loan.loanAmount.toLocaleString('vi-VN')}đ</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Ngày vay</p>
                    <p className="text-xs text-gray-600">{loan.startDate.slice(0, 10)}{loan.startDate.length > 10 ? ` · ${loan.startDate.slice(11, 16)}` : ''}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Hạn trả</p>
                    <p className="text-xs text-gray-600">{loan.dueDate}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Số ngày vay</p>
                    <p className="text-xs font-semibold text-gray-700">{borrowedDays(loan.startDate)} ngày</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setViewLoan(loan)} className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer text-gray-600 text-xs font-medium">
                    <i className="ri-eye-line text-sm"></i> Xem
                  </button>
                  <button onClick={() => setPrintLoan(loan)} className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors cursor-pointer text-amber-600 text-xs font-medium">
                    <i className="ri-printer-line text-sm"></i> In
                  </button>
                  <button onClick={() => openExtend(loan)} className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer text-blue-500 text-xs font-medium">
                    <i className="ri-time-line text-sm"></i> Gia hạn
                  </button>
                  <button onClick={() => setDeleteConfirm(loan.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer text-red-400">
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                  <button onClick={() => openConfiscate(loan)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-rose-50 transition-colors cursor-pointer text-rose-500">
                    <i className="ri-alarm-warning-line text-sm"></i>
                  </button>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <i className="ri-file-search-line text-4xl mb-2 block"></i>
              <p className="text-sm">Không tìm thấy khoản vay nào</p>
            </div>
          )}
        </div>

        {/* Desktop: Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Mã KV</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Khách hàng</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Tài sản</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Danh mục</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                  <div className="relative group inline-block">
                    <button
                      onClick={() => {
                        const cycle = ['all', ...warehouses];
                        const idx = cycle.indexOf(filterWarehouse);
                        setFilterWarehouse(cycle[(idx + 1) % cycle.length] || 'all');
                      }}
                      className={`flex items-center gap-1 cursor-pointer transition-colors ${filterWarehouse !== 'all' ? 'text-amber-600' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      Kho
                      {filterWarehouse !== 'all' && <span className="w-2 h-2 rounded-full bg-amber-500"></span>}
                      <span className="w-3 h-3 flex items-center justify-center"><i className={`ri-arrow-down-s-line text-[10px] ${filterWarehouse !== 'all' ? 'text-amber-500' : 'text-gray-400'}`}></i></span>
                    </button>
                    <div className="absolute top-full left-0 mt-0.5 bg-white border border-gray-200 rounded-xl shadow-lg z-30 py-1 min-w-[120px] hidden group-hover:block">
                      {[{ value: 'all', label: 'Tất cả' }, ...warehouses.map(k => ({ value: k, label: k }))].map(opt => (
                        <button
                          key={opt.value}
                          onClick={(e) => { e.stopPropagation(); setFilterWarehouse(opt.value); }}
                          className={`w-full text-left px-4 py-2 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap hover:bg-amber-50 ${filterWarehouse === opt.value ? 'text-amber-600 bg-amber-50' : 'text-gray-600'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Giá trị vay</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Lãi suất</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Ngày vay</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Số ngày vay</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Hạn trả</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Trạng thái</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(loan => {
                const st = statusConfig[loan.status];
                return (
                  <tr key={loan.id} className="hover:bg-amber-50/80 transition-colors cursor-default">
                    <td className="px-4 py-3.5 text-sm font-bold text-[#f59e0b]">{loan.id}</td>
                    <td className="px-4 py-3.5">
                      <p
                        className="text-sm font-medium text-gray-800 whitespace-nowrap cursor-pointer hover:text-amber-600 transition-colors"
                        onClick={() => {
                          const c = customers.find(cu => cu.id === loan.customerId);
                          if (c) setViewCustomer(c);
                        }}
                      >{loan.customerName}</p>
                      <p className="text-xs text-gray-400">{loan.customerPhone}</p>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-600 max-w-[160px] truncate">{loan.assetName}</td>
                    <td className="px-4 py-3.5"><span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full whitespace-nowrap">{loan.assetCategory}</span></td>
                    <td className="px-4 py-3.5">
                      <div className="relative">
                        <button
                          onClick={() => setEditingWarehouseFor(editingWarehouseFor === loan.id ? null : loan.id)}
                          className={`text-xs px-2 py-1 rounded-full whitespace-nowrap font-medium transition-colors cursor-pointer ${loan.warehouse ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'text-gray-300 hover:text-gray-500 hover:bg-gray-50'}`}
                        >
                          {loan.warehouse || '—'}
                          <i className="ri-arrow-down-s-line text-[10px] ml-0.5"></i>
                        </button>
                        {editingWarehouseFor === loan.id && (
                          <div className="absolute top-full left-0 mt-0.5 bg-white border border-gray-200 rounded-xl shadow-lg z-30 py-1 min-w-[120px]">
                            {[{ value: '', label: '— Không kho' }, ...warehouses.map(k => ({ value: k, label: k }))].map(opt => (
                              <button
                                key={opt.value}
                                onClick={() => handleChangeWarehouse(loan.id, opt.value)}
                                className={`w-full text-left px-4 py-2 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap hover:bg-amber-50 ${loan.warehouse === opt.value ? 'text-amber-600 bg-amber-50' : 'text-gray-600'}`}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-sm font-semibold text-gray-800 whitespace-nowrap">{loan.loanAmount.toLocaleString('vi-VN')}đ</td>
                    <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap">{loan.interestRate}%/tháng</td>
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <p className="text-sm text-gray-500">{loan.startDate.slice(0, 10)}</p>
                      {loan.startDate.length > 10 && <p className="text-xs text-gray-400">{loan.startDate.slice(11, 16)}</p>}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">{borrowedDays(loan.startDate)} ngày</span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-500 whitespace-nowrap">{loan.dueDate}</td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${st.className}`}>
                        {st.label}
                        {loan.status === 'overdue' && (
                          <span className="ml-1 opacity-90">
                            · {overdueDays(loan.dueDate)} ngày
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setViewLoan(loan)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-500" title="Xem chi tiết">
                          <i className="ri-eye-line text-sm"></i>
                        </button>
                        <button onClick={() => setPrintLoan(loan)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-amber-50 transition-colors cursor-pointer text-amber-500" title="In phiếu">
                          <i className="ri-printer-line text-sm"></i>
                        </button>
                        <button onClick={() => openExtend(loan)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-blue-50 transition-colors cursor-pointer text-blue-400" title="Gia hạn">
                          <i className="ri-time-line text-sm"></i>
                        </button>
                        <button onClick={() => setDeleteConfirm(loan.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer text-red-400" title="Xóa">
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                        <button onClick={() => openConfiscate(loan)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-rose-50 transition-colors cursor-pointer text-rose-500" title="Chuyển tịch thu">
                          <i className="ri-alarm-warning-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <i className="ri-file-search-line text-4xl mb-2 block"></i>
              <p className="text-sm">Không tìm thấy khoản vay nào</p>
            </div>
          )}
        </div>
      </div>

      {/* ===== MODAL: GIA HẠN ===== */}
      {extendLoan && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setExtendLoan(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-lg">
                  <i className="ri-time-line text-blue-600 text-base"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 text-sm">Gia Hạn Khoản Vay</h3>
                  <p className="text-gray-400 text-xs">{extendLoan.id} · {extendLoan.customerName}</p>
                </div>
              </div>
              <button onClick={() => setExtendLoan(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500 text-lg"></i>
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Thông tin hiện tại */}
              <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-2 gap-3">
                <div>
                  <p className="text-gray-400 text-xs mb-0.5">Tài sản cầm cố</p>
                  <p className="text-gray-800 text-sm font-medium truncate">{extendLoan.assetName}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-0.5">Giá trị vay</p>
                  <p className="text-gray-800 text-sm font-semibold">{extendLoan.loanAmount.toLocaleString('vi-VN')}đ</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-0.5">Hạn trả hiện tại</p>
                  <p className="text-red-500 text-sm font-semibold">{extendLoan.dueDate}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-0.5">Lãi suất</p>
                  <p className="text-gray-800 text-sm font-medium">{extendLoan.interestRate}%/tháng</p>
                </div>
              </div>

              {/* Chỉnh sửa ngày vay */}
              <div>
                <label className="text-xs text-gray-500 font-medium block mb-1.5">
                  Ngày vay
                  <span className="ml-1.5 text-amber-500 font-normal">(có thể chỉnh sửa)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 pointer-events-none">
                    <i className="ri-calendar-line text-sm"></i>
                  </span>
                  <input
                    type="date"
                    value={extendStartDate}
                    onChange={e => setExtendStartDate(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400 transition-colors"
                  />
                </div>
                {extendStartDate !== extendLoan.startDate && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <span className="w-3 h-3 flex items-center justify-center"><i className="ri-information-line"></i></span>
                    Ngày vay gốc: {extendLoan.startDate} — đã thay đổi
                  </p>
                )}
              </div>

              {/* Chọn loại gia hạn */}
              <div>
                <p className="text-xs text-gray-500 font-medium mb-2">Gia hạn theo</p>
                <div className="flex gap-2">
                  {[
                    { value: 'days', label: 'Số ngày' },
                    { value: 'months', label: 'Số tháng' },
                  ].map(opt => (
                    <button key={opt.value} onClick={() => { setExtendType(opt.value as 'days' | 'months'); setExtendValue(opt.value === 'days' ? 30 : 1); }}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${extendType === opt.value ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Nhập số ngày/tháng */}
              <div>
                <p className="text-xs text-gray-500 font-medium mb-2">
                  {extendType === 'days' ? 'Số ngày gia hạn' : 'Số tháng gia hạn'}
                </p>
                <div className="flex items-center gap-3">
                  <button onClick={() => setExtendValue(v => Math.max(1, v - (extendType === 'days' ? 1 : 1)))}
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer text-gray-700 font-bold text-lg">
                    -
                  </button>
                  <input type="number" min={1} value={extendValue}
                    onChange={e => setExtendValue(Math.max(1, Number(e.target.value)))}
                    className="flex-1 text-center px-3 py-2.5 border border-gray-200 rounded-lg text-lg font-bold text-gray-800 focus:outline-none focus:border-blue-400" />
                  <button onClick={() => setExtendValue(v => v + (extendType === 'days' ? 1 : 1))}
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer text-gray-700 font-bold text-lg">
                    +
                  </button>
                </div>
                {/* Quick select */}
                <div className="flex gap-2 mt-2">
                  {(extendType === 'days' ? [7, 14, 30, 60] : [1, 2, 3, 6]).map(v => (
                    <button key={v} onClick={() => setExtendValue(v)}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${extendValue === v ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      {v} {extendType === 'days' ? 'ngày' : 'tháng'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Hạn mới preview + Tính lợi nhuận */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                {/* Hạn mới */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-xs font-medium">Hạn trả mới sau gia hạn</p>
                    <p className="text-gray-800 font-bold text-base">
                      {addDaysToDate(extendLoan.dueDate, extendType === 'days' ? extendValue : extendValue * 30)}
                    </p>
                  </div>
                  <div className="w-9 h-9 flex items-center justify-center bg-gray-200 rounded-full">
                    <i className="ri-calendar-check-line text-gray-600 text-base"></i>
                  </div>
                </div>

                <div className="border-t border-gray-200"></div>

                {/* Lợi nhuận gia hạn */}
                {(() => {
                  const extDays = extendType === 'days' ? extendValue : extendValue * 30;
                  const interestPerDay = extendLoan.loanAmount * extendLoan.interestRate / 100 / 30;
                  const extInterest = Math.round(interestPerDay * extDays);
                  // Lãi từ đầu đến hạn cũ (dùng extendStartDate để tính đúng khi chỉnh ngày vay)
                  const startD = new Date(extendStartDate || extendLoan.startDate);
                  const oldDueD = new Date(extendLoan.dueDate);
                  const oldDays = Math.ceil((oldDueD.getTime() - startD.getTime()) / (1000 * 60 * 60 * 24));
                  const oldInterest = Math.round(interestPerDay * oldDays);
                  const totalInterest = oldInterest + extInterest;
                  const totalPayback = extendLoan.loanAmount + totalInterest;
                  return (
                    <div className="space-y-2">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Tính lợi nhuận (nội bộ)</p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Lãi kỳ cũ:</span>
                          <span className="font-semibold text-gray-700">{oldInterest.toLocaleString('vi-VN')}đ</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Lãi gia hạn thêm:</span>
                          <span className="font-semibold text-amber-600">{extInterest.toLocaleString('vi-VN')}đ</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 pt-1">
                        <div className="bg-amber-50 rounded-lg px-3 py-2 text-center">
                          <p className="text-xs text-gray-400 mb-0.5">Tổng lãi thu</p>
                          <p className="font-bold text-amber-600 text-sm">{totalInterest.toLocaleString('vi-VN')}đ</p>
                        </div>
                        <div className="bg-emerald-50 rounded-lg px-3 py-2 text-center">
                          <p className="text-xs text-gray-400 mb-0.5">Khách phải trả</p>
                          <p className="font-bold text-emerald-600 text-sm">{totalPayback.toLocaleString('vi-VN')}đ</p>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Ghi chú */}
              <div>
                <label className="text-xs text-gray-500 font-medium block mb-1">Ghi chú (tùy chọn)</label>
                <textarea value={extendNote} onChange={e => setExtendNote(e.target.value)} rows={2}
                  placeholder="Lý do gia hạn, thỏa thuận thêm..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400 resize-none" />
              </div>

              <div className="flex gap-3">
                <button onClick={() => setExtendLoan(null)} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Hủy</button>
                <button onClick={handleExtend} className="flex-1 py-2.5 bg-blue-500 text-white rounded-lg text-sm font-semibold hover:bg-blue-600 transition-colors cursor-pointer whitespace-nowrap">
                  Xác nhận gia hạn
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL: TẠO KHOẢN VAY MỚI ===== */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="font-bold text-gray-800">Tạo Khoản Vay Mới</h3>
              <button onClick={() => setShowAdd(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500 text-lg"></i></button>
            </div>
            <div className="p-6 space-y-5">

              {/* Chọn khách hàng */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Khách hàng *</label>
                  <button
                    onClick={() => { setShowNewCustomerInline(!showNewCustomerInline); setInlineCustomerForm(emptyCustomerForm); }}
                    className="flex items-center gap-1 text-xs text-[#f59e0b] font-medium hover:underline cursor-pointer whitespace-nowrap">
                    <i className="ri-user-add-line"></i>
                    {showNewCustomerInline ? 'Chọn khách có sẵn' : 'Tạo khách hàng mới'}
                  </button>
                </div>

                {!showNewCustomerInline ? (
                  <div className="relative">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400"><i className="ri-search-line text-sm"></i></span>
                      <input
                        value={customerSearch}
                        onChange={e => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); setLoanForm(prev => ({ ...prev, customerId: '', customerName: e.target.value, customerPhone: '' })); }}
                        onFocus={() => setShowCustomerDropdown(true)}
                        placeholder="Tìm tên hoặc số điện thoại..."
                        className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] transition-colors"
                      />
                    </div>
                    {showCustomerDropdown && customerSearch && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl overflow-hidden z-20 max-h-48 overflow-y-auto">
                        {filteredCustomers.length > 0 ? filteredCustomers.map(c => (
                          <button key={c.id} onClick={() => selectCustomer(c)}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-amber-50 transition-colors cursor-pointer text-left">
                            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                              <span className="text-amber-700 font-bold text-xs">{c.name.charAt(0)}</span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-800">{c.name}</p>
                              <p className="text-xs text-gray-400">{c.phone} · {c.id}</p>
                            </div>
                          </button>
                        )) : (
                          <div className="px-4 py-3 text-sm text-gray-400 text-center">Không tìm thấy khách hàng</div>
                        )}
                      </div>
                    )}
                    {loanForm.customerId && (
                      <div className="mt-2 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        <span className="w-4 h-4 flex items-center justify-center text-amber-600"><i className="ri-checkbox-circle-fill text-sm"></i></span>
                        <span className="text-xs text-amber-700 font-medium">{loanForm.customerName} · {loanForm.customerPhone}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Form tạo khách hàng mới inline */
                  <div className="bg-gray-50 rounded-xl p-4 space-y-3 border border-dashed border-gray-300">
                    <p className="text-xs text-gray-500 font-medium flex items-center gap-1.5">
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line text-amber-500"></i></span>
                      Tạo khách hàng mới
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: 'Họ tên *', key: 'name', span: 2 },
                        { label: 'Số điện thoại *', key: 'phone', span: 1 },
                        { label: 'CMND/CCCD', key: 'idCard', span: 1 },
                        { label: 'Địa chỉ', key: 'address', span: 2 },
                      ].map(f => (
                        <div key={f.key} className={f.span === 2 ? 'col-span-2' : ''}>
                          <label className="text-xs text-gray-500 font-medium block mb-1">{f.label}</label>
                          <input
                            value={(inlineCustomerForm as Record<string, unknown>)[f.key] as string}
                            onChange={e => setInlineCustomerForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] bg-white"
                          />
                        </div>
                      ))}
                    </div>
                    <button onClick={handleCreateInlineCustomer}
                      className="w-full py-2 bg-[#f59e0b] text-white rounded-lg text-sm font-semibold hover:bg-[#d97706] transition-colors cursor-pointer whitespace-nowrap">
                      Tạo &amp; chọn khách hàng này
                    </button>
                  </div>
                )}
              </div>

              {/* Thông tin khoản vay */}
              <div>
                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-3">Thông tin khoản vay</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="text-xs text-gray-500 font-medium block mb-1">Tên tài sản cầm cố *</label>
                    <input value={loanForm.assetName} onChange={e => setLoanForm(prev => ({ ...prev, assetName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium block mb-1">Danh mục</label>
                    <select value={loanForm.assetCategory} onChange={e => setLoanForm(prev => ({ ...prev, assetCategory: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] bg-white cursor-pointer">
                      {categories.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 font-medium block mb-1">Giá trị vay (đ) *</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={loanAmountStr}
                      onChange={e => {
                        const raw = e.target.value.replace(/\D/g, '');
                        const num = Number(raw) || 0;
                        setLoanAmountStr(raw ? Number(raw).toLocaleString('vi-VN') : '');
                        setLoanForm(prev => ({ ...prev, loanAmount: num }));
                      }}
                      placeholder="0"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]"
                    />
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {[500000, 1000000, 2000000, 3000000, 5000000, 10000000, 15000000, 20000000, 30000000, 50000000].map(amt => (
                        <button
                          key={amt}
                          type="button"
                          onClick={() => {
                            setLoanForm(prev => ({ ...prev, loanAmount: amt }));
                            setLoanAmountStr(amt.toLocaleString('vi-VN'));
                          }}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${loanForm.loanAmount === amt ? 'bg-[#f59e0b] text-white' : 'bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-700'}`}
                        >
                          {amt >= 1000000 ? `${amt / 1000000}tr` : `${amt / 1000}k`}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium block mb-1">Lãi suất (%/tháng)</label>
                    <input type="number" step="0.5" value={loanForm.interestRate} onChange={e => setLoanForm(prev => ({ ...prev, interestRate: Number(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]" />
                    <div className="flex gap-1.5 mt-2">
                      {[1, 1.5, 2, 2.5, 3, 5].map(rate => (
                        <button
                          key={rate}
                          type="button"
                          onClick={() => setLoanForm(prev => ({ ...prev, interestRate: rate }))}
                          className={`flex-1 py-1 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${loanForm.interestRate === rate ? 'bg-[#f59e0b] text-white' : 'bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-700'}`}
                        >
                          {rate}%
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium block mb-1">Ngày vay *</label>
                    <input type="date" value={loanForm.startDate} onChange={e => setLoanForm(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium block mb-1">Giờ vay</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 pointer-events-none">
                        <i className="ri-time-line text-sm"></i>
                      </span>
                      <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]" />
                    </div>
                    <div className="flex gap-1.5 mt-2">
                      {['07:00', '08:00', '09:00', '10:00', '14:00', '15:00', '16:00'].map(t => (
                        <button key={t} type="button" onClick={() => setStartTime(t)}
                          className={`flex-1 py-1 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${startTime === t ? 'bg-[#f59e0b] text-white' : 'bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-700'}`}>
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium block mb-1">Hạn trả *</label>
                    <input type="date" value={loanForm.dueDate} onChange={e => setLoanForm(prev => ({ ...prev, dueDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b]" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium block mb-1">Kho</label>
                    <select value={loanForm.warehouse} onChange={e => setLoanForm(prev => ({ ...prev, warehouse: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] bg-white cursor-pointer">
                      <option value="">— Không kho</option>
                      {warehouses.map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-1">
                <button onClick={() => setShowAdd(false)} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Hủy</button>
                <button onClick={handleAddLoan} className="flex-1 py-2.5 bg-[#f59e0b] text-white rounded-lg text-sm font-semibold hover:bg-[#d97706] cursor-pointer whitespace-nowrap">Lưu khoản vay</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL: TẠO KHÁCH HÀNG MỚI (standalone) ===== */}
      {showAddCustomer && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowAddCustomer(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg">
                  <i className="ri-user-add-line text-amber-600 text-base"></i>
                </div>
                <h3 className="font-bold text-gray-800">Tạo Khách Hàng Mới</h3>
              </div>
              <button onClick={() => setShowAddCustomer(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500 text-lg"></i></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Họ tên *', key: 'name', span: 2 },
                  { label: 'Số điện thoại *', key: 'phone', span: 1 },
                  { label: 'CMND/CCCD *', key: 'idCard', span: 1 },
                  { label: 'Địa chỉ', key: 'address', span: 2 },
                ].map(f => (
                  <div key={f.key} className={f.span === 2 ? 'col-span-2' : ''}>
                    <label className="text-xs text-gray-500 font-medium block mb-1">{f.label}</label>
                    <input
                      value={(customerForm as Record<string, unknown>)[f.key] as string}
                      onChange={e => setCustomerForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#f59e0b] transition-colors"
                    />
                  </div>
                ))}
              </div>
              <div className="flex gap-3 pt-1">
                <button onClick={() => setShowAddCustomer(false)} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Hủy</button>
                <button onClick={handleAddCustomer} className="flex-1 py-2.5 bg-[#f59e0b] text-white rounded-lg text-sm font-semibold hover:bg-[#d97706] cursor-pointer whitespace-nowrap">Lưu khách hàng</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewLoan && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => { setViewLoan(null); setEditingInterest(false); setEditingField(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="font-bold text-gray-800">Chi tiết khoản vay {viewLoan.id}</h3>
              <button onClick={() => { setViewLoan(null); setEditingInterest(false); setEditingField(null); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500 text-lg"></i></button>
            </div>
            <div className="p-6 space-y-4">
              {/* Helper render inline editable field */}
              {(() => {
                const renderField = (label: string, field: EditableField, displayValue: string, inputType: string = 'text') => {
                  const isEditing = editingField === field;
                  return (
                    <div key={field}>
                      <p className="text-gray-400 text-xs mb-0.5">{label}</p>
                      {isEditing ? (
                        <div className="space-y-1.5">
                          {field === 'assetCategory' ? (
                            <select
                              value={editFieldValue}
                              onChange={e => setEditFieldValue(e.target.value)}
                              autoFocus
                              className="w-full px-2 py-1.5 border border-[#f59e0b] rounded-lg text-sm font-semibold text-gray-800 focus:outline-none bg-white cursor-pointer">
                              {categories.map(c => <option key={c}>{c}</option>)}
                            </select>
                          ) : (
                            <input
                              type={field === 'loanAmount' ? 'text' : inputType}
                              inputMode={field === 'loanAmount' ? 'numeric' : undefined}
                              value={editFieldValue}
                              onChange={e => {
                                if (field === 'loanAmount') {
                                  const raw = e.target.value.replace(/\D/g, '');
                                  setEditFieldValue(raw ? Number(raw).toLocaleString('vi-VN') : '');
                                } else {
                                  setEditFieldValue(e.target.value);
                                }
                              }}
                              autoFocus
                              className="w-full px-2 py-1.5 border border-[#f59e0b] rounded-lg text-sm font-semibold text-gray-800 focus:outline-none"
                            />
                          )}
                          <div className="flex gap-1.5">
                            <button onClick={cancelEditField}
                              className="flex-1 py-1 border border-gray-200 rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                              Hủy
                            </button>
                            <button onClick={handleSaveField}
                              className="flex-1 py-1 bg-[#f59e0b] text-white rounded-lg text-xs font-semibold hover:bg-[#d97706] cursor-pointer whitespace-nowrap">
                              Lưu
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 group">
                          <p className="text-gray-800 text-sm font-medium">{displayValue}</p>
                          <button
                            onClick={() => openEditField(field, field === 'loanAmount' ? viewLoan.loanAmount : (viewLoan[field] as string))}
                            className="opacity-0 group-hover:opacity-100 w-5 h-5 flex items-center justify-center rounded-md hover:bg-amber-100 transition-all cursor-pointer text-amber-500"
                            title={`Chỉnh sửa ${label}`}>
                            <i className="ri-edit-line text-xs"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  );
                };

                return (
                  <div className="grid grid-cols-2 gap-4">
                    {renderField('Khách hàng', 'customerName', viewLoan.customerName)}
                    {renderField('Số điện thoại', 'customerPhone', viewLoan.customerPhone, 'tel')}
                    {renderField('Tài sản cầm cố', 'assetName', viewLoan.assetName)}
                    {renderField('Danh mục', 'assetCategory', viewLoan.assetCategory)}
                    {(viewLoan.assetCategory === 'Xe máy' || viewLoan.assetCategory === 'Ô tô') && (
                      <div>
                        <p className="text-gray-400 text-xs mb-0.5">Kho xe</p>
                        <p className="text-gray-800 text-sm font-medium">{viewLoan.warehouse || '—'}</p>
                      </div>
                    )}
                    {renderField('Giá trị vay', 'loanAmount', `${viewLoan.loanAmount.toLocaleString('vi-VN')}đ`, 'number')}
                    {renderField('Ngày vay', 'startDate', viewLoan.startDate.length > 10 ? `${viewLoan.startDate.slice(0, 10)} lúc ${viewLoan.startDate.slice(11, 16)}` : viewLoan.startDate, 'date')}
                    <div>
                      <p className="text-gray-400 text-xs mb-0.5">Số ngày đã vay</p>
                      <p className="text-gray-800 text-sm font-semibold">{borrowedDays(viewLoan.startDate)} ngày</p>
                    </div>
                    {renderField('Hạn trả', 'dueDate', viewLoan.status === 'overdue'
                      ? `${viewLoan.dueDate} (quá ${overdueDays(viewLoan.dueDate)} ngày)`
                      : viewLoan.dueDate, 'date')}

                    {/* Lãi suất - giữ nguyên UI cũ với quick select */}
                    <div>
                      <p className="text-gray-400 text-xs mb-0.5">Lãi suất</p>
                      {editingInterest ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              step="0.5"
                              min="0.1"
                              max="100"
                              value={editInterestValue}
                              onChange={e => setEditInterestValue(Number(e.target.value))}
                              className="w-24 px-2 py-1.5 border border-[#f59e0b] rounded-lg text-sm font-semibold text-gray-800 focus:outline-none"
                              autoFocus
                            />
                            <span className="text-sm text-gray-500">%/tháng</span>
                          </div>
                          <div className="flex gap-1.5 flex-wrap">
                            {[1, 1.5, 2, 2.5, 3, 5].map(rate => (
                              <button key={rate} type="button"
                                onClick={() => setEditInterestValue(rate)}
                                className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${editInterestValue === rate ? 'bg-[#f59e0b] text-white' : 'bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-700'}`}>
                                {rate}%
                              </button>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => setEditingInterest(false)}
                              className="flex-1 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                              Hủy
                            </button>
                            <button onClick={handleUpdateInterestRate}
                              className="flex-1 py-1.5 bg-[#f59e0b] text-white rounded-lg text-xs font-semibold hover:bg-[#d97706] cursor-pointer whitespace-nowrap">
                              Lưu
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 group">
                          <p className="text-gray-800 text-sm font-medium">{viewLoan.interestRate}%/tháng</p>
                          <button
                            onClick={() => { cancelEditField(); openEditInterest(viewLoan); }}
                            className="opacity-0 group-hover:opacity-100 w-5 h-5 flex items-center justify-center rounded-md hover:bg-amber-100 transition-all cursor-pointer text-amber-500"
                            title="Chỉnh sửa lãi suất">
                            <i className="ri-edit-line text-xs"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              <div>
                <p className="text-gray-400 text-xs mb-2">Cập nhật trạng thái</p>
                <div className="flex flex-wrap gap-2">
                  {(Object.keys(statusConfig) as Loan['status'][]).map(s => (
                    <button key={s} onClick={() => handleStatusChange(viewLoan.id, s)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors whitespace-nowrap ${viewLoan.status === s ? statusConfig[s].className + ' ring-2 ring-offset-1 ring-[#f59e0b]' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      {statusConfig[s].label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Lịch sử chỉnh sửa */}
              <LoanHistoryPanel loanId={viewLoan.id} />
              {/* Nút hành động trong view modal */}
              <div className="pt-1 border-t border-gray-100 flex gap-2">
                <button onClick={() => { setViewLoan(null); setPrintLoan(viewLoan); }}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-amber-50 text-amber-600 rounded-xl text-sm font-semibold hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-printer-line text-base"></i></span>
                  In phiếu
                </button>
                <button onClick={() => { setViewLoan(null); openExtend(viewLoan); }}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-50 text-blue-600 rounded-xl text-sm font-semibold hover:bg-blue-100 transition-colors cursor-pointer whitespace-nowrap">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-time-line text-base"></i></span>
                  Gia hạn
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* In phiếu */}
      {printLoan && <PrintLoanTicket loan={printLoan} customers={customers} onClose={() => setPrintLoan(null)} />}

      {/* Inventory Modal */}
      <InventoryModal open={showInventoryModal} onClose={() => setShowInventoryModal(false)} />

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="font-bold text-gray-800 text-center mb-2">Xác nhận xóa?</h3>
            <p className="text-gray-500 text-sm text-center mb-2">Khoản vay sẽ bị xóa vĩnh viễn khỏi hệ thống.</p>
            <p className="text-red-400 text-xs text-center mb-5 font-medium">Tài sản tồn kho và doanh thu liên kết cũng sẽ bị xóa theo.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Hủy</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 py-2.5 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 cursor-pointer whitespace-nowrap">Xóa</button>
            </div>
          </div>
        </div>
      )}

      {/* Customer View Modal */}
      {viewCustomer && (
        <CustomerViewModal
          customer={viewCustomer}
          loans={loans}
          onClose={() => setViewCustomer(null)}
        />
      )}

      {loading && loans.length === 0 && (
        <div className="text-center py-12 text-gray-400 mb-4">
          <i className="ri-loader-4-line text-3xl animate-spin block mb-2"></i>
          <p className="text-sm">Đang tải dữ liệu...</p>
        </div>
      )}
    </>
  );
}
